import { API_BASE_URL } from '../config/serverApiConfig';
import { api } from './api';

export interface GroupHomeNode {
  id: number;
  name: string;
  kind: string;
}

export interface GroupMember {
  id: string;
  email: string;
  name: string;
}

export interface Group {
  id: number;
  name: string;
  description: string;
  archived: boolean;
  organisation_id: number;
  home_node: GroupHomeNode | null;
  editable: boolean;
  members: GroupMember[];
  assignments: { id: number; name: string; workspace_id: number }[];
}

export interface GroupList {
  groups: Group[];
  inherited: Group[];
}

export interface GroupInput {
  name: string;
  description: string;
  member_ids: string[];
  archived: boolean;
}

const fresh = { cache: false } as any;
const base = (nodeId: number) => `${API_BASE_URL}r/v2/workspaces/${nodeId}/groups`;

export const listGroups = async (nodeId: number): Promise<GroupList> => {
  const response = await api().get(base(nodeId), fresh);
  return {
    groups: response.data?.groups ?? [],
    inherited: response.data?.inherited ?? []
  };
};

export const listGroupCandidates = async (nodeId: number): Promise<{ id: string; email: string }[]> => {
  const response = await api().get(`${API_BASE_URL}r/v2/workspaces/${nodeId}/group-candidates`, fresh);
  return response.data ?? [];
};

export const createGroup = async (nodeId: number, input: GroupInput): Promise<Group> =>
  (await api().post(base(nodeId), input)).data;

export const updateGroup = async (nodeId: number, groupId: number, input: GroupInput): Promise<Group> =>
  (await api().put(`${base(nodeId)}/${groupId}`, input)).data;

export const deleteGroup = async (nodeId: number, groupId: number): Promise<void> => {
  await api().delete(`${base(nodeId)}/${groupId}`);
};

/** A readable message for a failed group call (409 duplicate, 403, 422 and the server's own text). */
export const groupErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const text = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail[0]?.msg : undefined;
  if (status === 409) return text || 'A group with this name already exists.';
  if (status === 403) return 'You do not have permission to manage groups here.';
  if (status === 422) return text ? `Check the form: ${text}` : 'Check the form: the name is required.';
  if (status === 404) return 'This group no longer exists. Refresh the page.';
  return text || 'Something went wrong. Please try again.';
};

const LEVEL_OF_KIND: Record<string, 'organisation' | 'entity' | 'workspace'> = {
  organisation: 'organisation',
  entity: 'entity',
  workspace: 'workspace'
};

export const groupLevel = (kind: string | undefined) => (kind ? LEVEL_OF_KIND[kind.toLowerCase()] : undefined);

/**
 * The "Belongs to" cell: "This <level>" for a group homed at the node being viewed, otherwise
 * the home node's level and name.
 */
export const belongsTo = (
  group: Pick<Group, 'home_node'>,
  currentNodeId: number,
  currentLevel: 'organisation' | 'entity' | 'workspace'
): { own: boolean; text: string; level?: 'organisation' | 'entity' | 'workspace' } => {
  const home = group.home_node;
  if (!home) return { own: false, text: '-' };
  if (home.id === currentNodeId) {
    const word = currentLevel[0].toUpperCase() + currentLevel.slice(1);
    return { own: true, text: `This ${word.toLowerCase()}` };
  }
  return { own: false, text: home.name, level: groupLevel(home.kind) };
};
